import { MentionsInput, Mention } from "react-mentions";
import React, { useEffect, useRef, useState } from "react";
import CustomAvatar from "../../../Core/Common/CustomAvatar";
import CustomButton from "../../../Core/Common/CustomButton";
import { Dropdown, Menu, Progress, Tooltip, Upload, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../../Core/redux/hooks";
import {
  resetConversations,
  updateConversations,
  addMessage,
  updateAllChatLastMessage,
  updateMessageContent,
} from "../../../Core/redux/slices/conversations.slice";

import { ObjectID } from "bson";
import axios from "axios";
import uniqid from "uniqid";
import { updateVault } from "../../../Core/redux/slices/vault.slice";
import {
  saveComments,
  updateComment,
} from "../../../Core/redux/api/conversationAPI";
import { sendMessageAPI } from "../../../Core/redux/api/v3/conversationAPI";

import CustomEmojiPicker from "../../../Core/Common/CustomEmojiPicker";
import { editMessageAPI } from "../../../Core/redux/api/v3/conversationAPI";
import { AtIcon } from "../../../Core/svgV2/AtIcon";
import { AttachmentIcon } from "../../../Core/svgV2/AttachmentIcon";
import { CrossIcon } from "../../../Core/svgV2/CrossIcon";
import RequestFlow from "./RequestFlow";
import ReplyMessageFlow from "./ReplyMessageFlow";
import UploadFromLibrary from "../Modals/UploadFromLibrary";
import useFileList from "./useFileList";
import { useParams } from "react-router-dom";
import {
  buildMentionableUsers,
  extractMentionedIds,
  expandMentions,
  MENTION_ALL_ID,
} from "../../../Core/utils/mentionUtils";

function isImageFilename(filename) {
  const imageExtensions = /\.(jpe?g|png|gif|bmp|webp)$/i;
  return imageExtensions.test(filename);
}

// Resolves to the pixel count (width * height) of an image file, used to
// flag oversized images after upload. Never rejects — an unreadable or
// non-image file just resolves to 0, which simply means the size-bound
// check below won't trigger for it, instead of blowing up the upload.
function getImageDimensions(file): Promise<number> {
  return new Promise((resolve) => {
    if (!file?.type?.startsWith?.("image/")) {
      resolve(0);
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => resolve(0);
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => resolve(img.width * img.height);
      img.onerror = () => resolve(0);
      img.src = event.target.result.toString();
    };
    reader.readAsDataURL(file);
  });
}

const mentionInputStyle = {
  control: {
    fontSize: 14,
    lineHeight: "20px",
    fontFamily: "Outfit, sans-serif",
    minHeight: 52,
  },
  "&multiLine": {
    control: { minHeight: 52 },
    highlighter: { padding: 12, border: "1px solid transparent" },
    input: {
      padding: 12,
      border: "1px solid transparent",
      outline: "none",
      minHeight: 52,
    },
  },
  suggestions: {
    parent: { position: "relative" },
    list: {
      position: "top",
      zIndex: 50,
      width: "100%",
      minWidth: 260,
      maxHeight: 240,
      overflowY: "auto",
      backgroundColor: "#fff",
      marginBottom: 6,
      padding: 6,
    },
    item: {
      padding: 0,
      "&focused": { backgroundColor: "#F9FAFB" },
    },
  },
} as const;

const ConversationsInput = ({
  socket,
  comments = false,
  allowRequest = true,
  isMyBoard = false,
}: {
  socket?: any;
  comments?: boolean;
  allowRequest?: boolean;
  isMyBoard?: boolean;
}) => {
  const {
    editMessage: { edit, messageObject },
    currentThread,
    currentThreadMessage,
    replyMessage,
    directMessageUsers,
    threadMembers,
    threads,
    requestOption,
    chatRequest,
  } = useAppSelector((state) => state.conversations);

  const { openCommentsModal } = useAppSelector((state) => state.vault);
  const { user } = useAppSelector((state) => state.userDetails);
  const { taskId, subtaskId } = useParams();
  const { tenantMembers } = useAppSelector((state) => state.tenant);
  const { userList } = useAppSelector((state) => state.overview);
  const { currentSelectedTask } = useAppSelector((state) => state.myBoard);

  const [moreOptions, setMoreOptions] = useState(true);
  const [value, setValue] = useState(
    edit ? (messageObject.message ?? messageObject.content ?? "") : ""
  );
  const [uploadFromVault, setUploadFromVault] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const { selectedTask } = useAppSelector((state) => state.schedule);

  const isThread = currentThread?.threadId;

  React.useEffect(() => {
    if (edit) {
      const text = messageObject.message ?? messageObject.content ?? "";
      setValue(text);
      setMoreOptions(true);
      setTimeout(() => {
        if (inputEl?.current) {
          inputEl.current.focus();
          const len = text.length || 0;
          try {
            inputEl.current.setSelectionRange(len, len);
          } catch (e) {
            // some underlying input types don't support setSelectionRange
          }
        }
      }, 0);
    } else {
      setValue("");
    }
  }, [edit, messageObject]);

  const setRequestOption = (val) => {
    dispatch(updateConversations({ key: "requestOption", value: val }));
  };

  React.useEffect(() => {
    if (replyMessage.reply) {
      setMoreOptions(true);
      // Same idea as the edit-focus effect above: after a reply is picked,
      // the cursor should already be blinking in the input rather than
      // requiring an extra click. Deferred one tick so it runs after the
      // reply preview has mounted and shifted layout.
      setTimeout(() => {
        if (inputEl?.current) {
          inputEl.current.focus();
        }
      }, 0);
    }
  }, [replyMessage]);

  const [fileUploadedList, setFileUploadedList] = useState([]);

  const {
    fileState,
    uploadingState: isFileUploading,
    setUploadingState: setIsFileUploading,
    setFileState,
  } = useFileList();

  // Single entry point for turning fileState into fileUploadedList,
  // regardless of whether files arrived via the picker (handleChange
  // below) or from the vault (handelUploadFromVault, further down) —
  // both paths just call setFileState, and this effect reacts to that.
  useEffect(() => {
    if (!fileState) return;

    const fileData = [];
    fileState.forEach((file) => {
      const { name, size, type, lastModified, status } = file;
      if (status !== "done") return;
      file.response.forEach((xfile) => {
        const { fileUrl: url, thumbnail } = xfile;
        fileData.push({
          name,
          url,
          size,
          type,
          lastModified,
          savedInVault: Boolean(file.savedInVault),
          thumbnail: thumbnail.fileUrl,
          imageOutOfBound: xfile?.imageOutOfBound ? true : false,
        });
      });
    });
    setFileUploadedList(fileData);
  }, [fileState]);

  const handleChange = ({ fileList: newFileList }) => {
    setFileState([...newFileList]);
  };

  let className = "flex items-center border-gray-200";
  if (moreOptions || edit || replyMessage.reply) {
    className = "";
  }
  if (value !== "" || replyMessage.reply) {
    if (edit) className = className + "border-[#FEC84B]";
    else className = className + "border-[#7DC7FF]";
  }

  let users =
    taskId || subtaskId
      ? isMyBoard
        ? tenantMembers
          ?.filter((user) =>
            currentSelectedTask?.collaborators?.includes(user.id)
          )
          .map((user) => ({
            id: user.id,
            display: user.firstname
              ? user.firstname + " " + user.lastname
              : user.email,
          }))
        : userList?.map((user) => ({
          id: user.userId,
          display: user.firstname
            ? user.firstname + " " + user.lastname
            : user.email,
        }))
      : threadMembers?.map((user) => ({
        id: user.userId,
        display: user.firstname
          ? user.firstname + " " + user.lastname
          : user.email,
      }));
  // buildMentionableUsers replaces a plain `.filter(self out)` here: it
  // does the same self-filter, but also (a) tolerates `users` still being
  // undefined while its source list hasn't loaded yet — the old
  // `users.filter(...)` threw in that case — and (b) prepends the "All"
  // suggestion once there's an actual group to mention.
  users = buildMentionableUsers(users, user.id);

  const uploadRef = useRef();
  const uploadButton = useRef<HTMLButtonElement>(null);
  const inputEl = useRef(null);

  const onEmojiClick = (emojiObject) => {
    const { emoji } = emojiObject;
    setValue((prevValue) => `${prevValue || ""} ${emoji}`);
  };
  const dispatch = useAppDispatch();

  const { tenantDetails } = useAppSelector((state) => state.tenant);

  const userLocal = JSON.parse(localStorage.getItem("user"));

  const uploadImage = (options: any) => {
    setIsFileUploading(true);
    const { onSuccess, onError, file, onProgress } = options;

    const fmData = new FormData();
    const config = {
      headers: {
        "content-type": "multipart/form-data",
        Authorization: "Bearer " + userLocal.token,
      },
      onUploadProgress: (event) => {
        onProgress({ percent: (event.loaded / event.total) * 100 });
      },
    };
    const fileFormData = new FormData();
    fileFormData.append(
      "file",
      file,
      uniqid(`${file.name.split(".")[0]}-`, `.${file.name.split(".").at(-1)}`)
    );
    const _file = fileFormData.get("file");
    fmData.append("image", _file);

    // Uploading and reading the image's own dimensions used to run
    // independently, with the upload's .then() reading whatever
    // `dimensionalValue` happened to be *at that moment* — almost always
    // still 0, since FileReader + Image decoding is asynchronous and the
    // network request routinely finishes first, especially for the large
    // images this check exists to catch. imageOutOfBound was effectively
    // never set. Running both and waiting on both fixes that.
    Promise.all([
      getImageDimensions(file),
      axios.post(
        process.env.REACT_APP_API_BASE_URL + "/v1/upload/storej",
        fmData,
        config
      ),
    ])
      .then(([dimensionalValue, res]) => {
        let newResObj = { ...res.data[0] };
        if (+dimensionalValue >= 71000000) {
          newResObj = { ...newResObj, imageOutOfBound: true };
        }
        onSuccess([newResObj]);
        setIsFileUploading(false);
      })
      .catch((err) => {
        // Previously this whole call was wrapped in a synchronous
        // try/catch, which can't catch anything thrown inside a .then()
        // callback — a failed request (or an error reading res.data)
        // became an unhandled rejection: onError never fired, and
        // isFileUploading never got reset to false. Since sendMessage,
        // sendCommentsMessage, and editMessage all bail out early while
        // isFileUploading is true, one failed upload could silently
        // block the input from sending anything, text included, until
        // the page was reloaded.
        onError({ err });
        setIsFileUploading(false);
      });
  };

  const handleRequestOption = (message) => {
    if (requestOption) {
      const request = {
        type: chatRequest.selectedRequestType,
        status: "Open",
        requestClosingDate: null,
        urgency: chatRequest.selectedUrgency,
        deadlineDate: chatRequest.deadlineDate,
        deadlineTime: chatRequest.deadlineTime,
        activity: [],
        updateCount: 0,
        updateSeenUsers: [],
      };
      return { ...message, request };
    }
    return { ...message, request: null };
  };

  const sendMessage = () => {
    if (isFileUploading) return;
    if (value?.toString().trim().length === 0 && fileState.length === 0) return;

    const { firstName, lastName, id, profilePicture } = user;
    const objectID = new ObjectID();

    // mentions carries the resolved set of mentioned member ids (with
    // "@all" expanded to every thread member) as its own field, separate
    // from notifyToUsers below. notifyToUsers already notifies the whole
    // thread for every message regardless of @mentions, so this doesn't
    // change delivery — it adds the data needed to show "you were
    // mentioned" highlighting distinctly from a regular new-message ping.
    // NOTE: this is a new field on the message payload — worth confirming
    // the backend/socket handler on your end passes it through (or at
    // least doesn't choke on an unrecognized field) before relying on it.
    const mentions = expandMentions(extractMentionedIds(value), users);

    let message = {
      _id: objectID.toString(),
      threadId: currentThread.threadId,
      content: value.trim(),
      message: value.trim(),
      senderName: `${firstName} ${lastName}`,
      senderId: id,
      sender: id,
      createdAt: dayjs().utc().format(),
      senderProfilePicture: profilePicture,
      replyMessage: null,
      reactions: {},
      attachments: fileUploadedList,
      mentions,
      notifyToUsers: threadMembers
        .filter((m) => m.userId !== user.id)
        .map((m) => ({ userId: m.userId })),
      projectId: currentThread.projectId,
      threadName: currentThread.threadName,
      tenantId: tenantDetails.tenantId,
      isGroup: currentThread.type === "group",
    };

    if (replyMessage.reply) {
      message.replyMessage = {
        ...replyMessage.messageObject,
        replyMessage: undefined,
      };
    }
    message = handleRequestOption(message);

    if (
      replyMessage.reply &&
      replyMessage.messageObject.request &&
      replyMessage.messageObject.request?.type
    ) {
      const messageListCopy = currentThreadMessage.map((m) => {
        if (m._id === replyMessage.messageObject._id) {
          return {
            ...m,
            request: {
              ...m.request,
              updateCount: 1,
              activity: [
                ...m.request.activity,
                { ...message, replyMessage: {}, request: {} },
              ],
            },
          };
        }
        return m;
      });
      dispatch(
        updateConversations({
          key: "currentThreadMessage",
          value: [...messageListCopy],
        })
      );
      socket.emit("replyToRequest", {
        parentMessageId: replyMessage.messageObject._id,
        message,
      });
    } else {
      const chatIdStr = String(currentThread.threadId);

      dispatch(addMessage({ chatId: chatIdStr, message }));

      dispatch(
        updateAllChatLastMessage({
          chatId: chatIdStr,
          value: message.content || "Attachment",
        })
      );

      sendMessageAPI({ message, forwarded: false });

      dispatch(
        updateConversations({
          key: "currentThreadMessage",
          value: [...currentThreadMessage, message],
        })
      );
    }

    // ── Cleanup ─────────────────────────────────────────────────────────────
    setValue("");
    setFileState([]);
    setFileUploadedList([]);
    dispatch(
      resetConversations([
        "replyMessage",
        "chatRequest",
        "requestOption",
        "defaultFileList",
      ])
    );
    localStorage.removeItem(
      `${currentThread.type === "group"
        ? currentThread.threadId
        : currentThread.userId
      }-${tenantDetails.tenantId}`
    );
  };

  const editMessage = async () => {

    if (isFileUploading) return;
    if (value?.toString().trim().length === 0) return;
    if (messageObject?.forwarded) return;
    const trimmedValue = value.trim();
    const mentions = expandMentions(extractMentionedIds(trimmedValue), users);

    if (comments) {
      dispatch(updateComment({ messageId: messageObject._id, message: trimmedValue }));
      const applyEdit = (oldMessages) =>
        oldMessages.map((m) =>
          m._id === messageObject._id ? { ...m, message: trimmedValue } : m
        );
      const edited = applyEdit(openCommentsModal.comments);
      dispatch(
        updateVault({
          key: "openCommentsModal",
          value: { ...openCommentsModal, comments: edited },
        })
      );
      setValue("");
      dispatch(resetConversations(["replyMessage"]));
      return;
    }

    const chatIdStr = String(currentThread.threadId);

    dispatch(
      updateMessageContent({
        chatId: chatIdStr,
        messageId: messageObject._id,
        message: trimmedValue,
        attachments: fileUploadedList,
        mentions,
      })
    );

    socket.emit("editMessage", {
      messageId: messageObject._id,
      threadId: currentThread.threadId,
      message: trimmedValue,
      mentions,
    });

    localStorage.removeItem(
      `${currentThread.type === "group" ? currentThread.threadId : currentThread.userId}-${tenantDetails.tenantId}`
    );

    setValue("");
    dispatch(resetConversations(["replyMessage"]));

    try {
      await editMessageAPI({
        messageId: messageObject._id,
        updatedMessage: trimmedValue,
        attachments: fileUploadedList,
        mentions,
      });
    } catch (e) {
      console.log(e);
    }
  };

  const sendCommentsMessage = async () => {

    if (isFileUploading) return;
    if (value?.toString().trim().length === 0 && fileState.length === 0) return;

    const mentionedIds = extractMentionedIds(value);
    const expandedMentionedIds = expandMentions(mentionedIds, users);
    const mentionedUsers = expandedMentionedIds.map((userId) => ({ userId }));

    const sendNotificationToUsers = [
      ...openCommentsModal.users,
      ...mentionedUsers.filter(
        (existingUser) =>
          !openCommentsModal.users.some(
            (mentionedUser) =>
              +existingUser.userId === +mentionedUser.userId
          )
      ),
    ];

    const { firstName, lastName, id, profilePicture } = user;
    const objectID = new ObjectID();

    const message = {
      _id: objectID.toString(),
      docId: openCommentsModal.docId,
      docType: openCommentsModal.docType,
      message: value.trim(),
      senderName: `${firstName} ${lastName}`,
      senderId: id,
      createdAt: dayjs().utc().format(),
      senderProfilePicture: profilePicture,
      replyMessage: null,
      reactions: {},
      attachments: fileUploadedList,
      users: sendNotificationToUsers,
      name: openCommentsModal.name,
      meta: {
        projectId: selectedTask?.projectId,
        url: window.location.href,
        name: openCommentsModal.name,
        sprintId: selectedTask?.sprintId,
        taskName: selectedTask?.name,
        taskId: selectedTask?.id,
      },
    };

    if (replyMessage.reply) {
      message.replyMessage = {
        ...replyMessage.messageObject,
        replyMessage: undefined,
      };
    }

    dispatch(
      updateVault({
        key: "openCommentsModal",
        value: {
          ...openCommentsModal,
          comments: [...openCommentsModal.comments, message],
        },
      })
    );

    setValue("");
    setFileState([]);
    setFileUploadedList([]);
    dispatch(resetConversations(["replyMessage"]));
    await dispatch(saveComments({ message }));
  };

  const handleUserTyping = (isTyping) => {
    if (comments) return;
    const { firstName, lastName } = user;

    const payload = {
      isTyping,
      projectId: 1,
      threadId:
        currentThread.type === "group"
          ? currentThread.threadId
          : currentThread.userId,
      userName: `${firstName} ${lastName}`,
      userId: user.id,
      threadType: currentThread.type,
    };

    socket.emit("user-typing", payload);
  };

  React.useEffect(() => {
    if (!comments) {
      const draft = localStorage.getItem(
        `${currentThread.type === "group"
          ? currentThread.threadId
          : currentThread.userId
        }-${tenantDetails.tenantId}`
      );
      setValue(draft ? draft : "");
    }
  }, [currentThread]);

  const handelUploadFromVault = (arr) => {
    const uploadArr = arr.map((a) => ({
      name: a.name,
      url: a.url,
      size: a.fileSize,
      type: a.mimeType,
      lastModified: "",
      savedInVault: true,
      status: "done",
      response: [{ fileUrl: a.url, thumbnail: { fileUrl: a.thumbnail } }],
    }));
    setFileState([...uploadArr]);
  };

  const handleScroll = () => {
    const scope = inputEl?.current?.closest?.(".comments-textarea") ?? document;
    const selectedElement = scope.querySelector('[aria-selected="true"]');
    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  };

  const REQUESTS_ENABLED = false;
  return (
    <div id="conversations-tour-input-request">
      <div
        className={`relative min-h-[52px] w-full bg-white border transition-all rounded-lg ${className}`}
        onClick={() => setMoreOptions(true)}
        id="conversations-tour-input"
      >
        {replyMessage.reply && <ReplyMessageFlow replyMessage={replyMessage} />}
        {requestOption ? <RequestFlow /> : null}
        <div
          className={`${fileState.length === 0 ? "" : "rounded-lg overflow-hidden"}`}
        >
          <Upload
            listType="picture-card"
            multiple
            action={process.env.REACT_APP_API_URL + "/v1/upload"}
            headers={{ Authorization: "Bearer " + user.token }}
            customRequest={uploadImage}
            ref={uploadRef}
            fileList={[...fileState]}
            onChange={handleChange}
            itemRender={(originNode, file, fileList, actions) => {
              const { response, status, percent } = file;
              const fileUrl =
                response && response.length > 0
                  ? isImageFilename(response[0]?.fileName)
                    ? response[0]?.fileUrl
                    : response[0]?.thumbnail?.fileUrl || ""
                  : "";

              return (
                <div
                  className="h-[56px] w-[56px] bg-no-repeat bg-center bg-cover rounded-lg relative flex justify-center items-center"
                  style={{
                    backgroundImage: `linear-gradient(0deg, rgba(16, 24, 40, 0.2), rgba(16, 24, 40, 0.2)), url(${fileUrl || file.url
                      })`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                  }}
                >
                  {percent < 100 && status !== "done" ? (
                    <Progress
                      type="circle"
                      trailColor="#e6f4ff"
                      percent={percent}
                      strokeWidth={10}
                      width={40}
                      format={() => ""}
                      className="progress-upload"
                    />
                  ) : percent === 100 && status !== "done" ? (
                    <Spin
                      indicator={<LoadingOutlined style={{ fontSize: 40 }} />}
                    />
                  ) : (
                    <></>
                  )}
                  <div
                    className="absolute flex items-center justify-center w-5 h-5 bg-gray-900 rounded-full cursor-pointer -right-1 -top-1"
                    onClick={actions.remove}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      fill="none"
                      viewBox="0 0 20 20"
                    >
                      <path
                        stroke="#fff"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 5L5 15M5 5l10 10"
                      ></path>
                    </svg>
                  </div>
                </div>
              );
            }}
            style={fileState.length === 0 && { display: "none" }}
            className={`${fileState.length === 0 ? "custom-upload-hide" : "custom-upload"
              } `}
          >
            {fileState.length === 0 ? (
              <button
                className=""
                style={{ display: "none" }}
                ref={uploadButton}
              />
            ) : (
              <div>
                <img src="/images/icons/plus-gray.svg" />
              </div>
            )}
          </Upload>
        </div>

        {uploadFromVault && (
          <UploadFromLibrary
            visible={uploadFromVault}
            onClose={() => setUploadFromVault(false)}
            onChange={(arr) => handelUploadFromVault(arr)}
          />
        )}

        <MentionsInput
          value={value}
          inputRef={inputEl}
          className="comments-textarea"
          style={mentionInputStyle}
          forceSuggestionsAboveCursor={true}
          onChange={(e) => {
            setValue(e.target.value);
            handleUserTyping(true);
            localStorage.setItem(
              `${currentThread.type === "group"
                ? currentThread.threadId
                : currentThread.userId
              }-${tenantDetails.tenantId}`,
              e.target.value
            );
            clearTimeout(timeoutId);
            setTimeoutId(
              setTimeout(() => {
                handleUserTyping(false);
              }, 100)
            );
          }}
          onBlur={() => {
            handleUserTyping(false);
          }}
          autoFocus={true}
          id="conversation-chat-input"
          onPaste={(e) => {
            function getFileFromPasteEvent(event) {
              const items = (
                event.clipboardData || event.originalEvent.clipboardData
              ).items;
              for (const index in items) {
                const item = items[index];
                if (item.kind === "file") {
                  return item.getAsFile();
                }
              }
            }
            const file = getFileFromPasteEvent(e);
            if (!file) return;
          }}

          placeholder={
            comments
              ? "Add a comment"
              : requestOption
                ? "Mention the requestee by typing the “@” and selecting the contact's name from the list."
                : "Type a message"
          }
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              // react-mentions moves aria-selected to the next suggestion
              // synchronously on this same keydown, but the DOM update it
              // causes lands right after — deferring a tick lets
              // handleScroll find the newly-selected row before scrolling
              // to it. Matters more now that the list has an extra "All"
              // row to scroll past.
              setTimeout(handleScroll, 0);
            }
            if (e.key === "/" && value.length === 0) {
              if (comments) return true;
              if (!REQUESTS_ENABLED || !allowRequest) return true;
              setRequestOption(true);
              e.stopPropagation();
              e.preventDefault();
              return false;
            }
            if (e.key === "Enter" && (e.ctrlKey || e.shiftKey)) {
              return true;
            }
            if (e.key === "Enter" && !isFileUploading) {
              dispatch(resetConversations(["unreadCount"]));
              e.preventDefault();
              if (edit) {
                editMessage();
                dispatch(resetConversations(["editMessage"]));
                return true;
              }
              comments ? sendCommentsMessage() : sendMessage();
              return true;
            }
          }}
        >
          <Mention
            trigger="@"
            // Without this, react-mentions treats a space as "query over"
            // and closes/breaks the suggestions list the moment you type
            // past the first word of a two-word name (e.g. "@Jett Kor").
            // That's why single-word filtering looked fine but two-word
            // filtering appeared to "hang" — the query was silently being
            // terminated at the space, not actually filtering on it.
            allowSpaceInQuery
            data={taskId || subtaskId ? users : isThread ? users : []}
            markup="@@@____id__^^____display__@@@^^^"
            style={{
              color: "#165ABF",
              fontWeight: 300,
              position: "relative",
              zIndex: "1",
            }}
            appendSpaceOnAdd
            displayTransform={(id, text) => `@${text}`}
            renderSuggestion={(entry, search, highlightedDisplay, index, focused) => (
              <div
                key={entry.display}
                onMouseEnter={(e) => e.stopPropagation()}
                className={`flex items-center px-3 py-2 rounded-md transition-colors ${focused ? "bg-gray-50" : ""
                  }`}
              >
                <CustomAvatar title={entry.display} size={28} />
                <span
                  className={`ml-2.5 text-gray-700 capitalize truncate ${entry.id === MENTION_ALL_ID ? "mon-500-14" : "mon-400-14"
                    }`}
                >
                  {entry.display}
                </span>
              </div>
            )}
          />
        </MentionsInput>

        <div
          className={` justify-between items-center px-2 pb-2 ${moreOptions || edit || replyMessage.reply ? "flex" : "hidden"
            }`}
        >
          <div className="flex items-center pl-2 gap-x-4">
            <Tooltip title="Mention someone">
              <button
                onClick={() => {
                  setValue(`${value} @`);
                  inputEl?.current?.focus();
                }}
              >
                <AtIcon className="text-gray-700" />
              </button>
            </Tooltip>

            <CustomEmojiPicker onChange={onEmojiClick} className="" />
            {!comments && (
              <Dropdown
                className=""
                overlay={
                  <Menu className="w-[225px] rounded-lg custom-menu-dropdown  shadow-dropdown">
                    <Menu.Item
                      className=" py-2.5 px-4 rounded-t-lg"
                      key="from-vault"
                    >
                      <div
                        className="flex items-center"
                        onClick={() => setUploadFromVault(true)}
                      >
                        <img
                          src="/images/icons/folder-dark-border.svg"
                          className="w-4 h-4"
                          alt="add-new-sprint"
                        />
                        <span className="ml-2 text-gray-700 mon-400-12 whitespace-nowrap">
                          From Library
                        </span>
                      </div>
                    </Menu.Item>
                    <Menu.Item
                      className="py-2.5 px-4 rounded-b-lg"
                      key="from-computer"
                    >
                      <div
                        className="flex items-center "
                        onClick={() => {
                          if (uploadButton && uploadButton.current) {
                            uploadButton.current.click();
                          }
                        }}
                      >
                        <img
                          src="/images/icons/monitor-01.svg"
                          alt="add-new-sprint"
                          className="w-4 h-4"
                        />
                        <span className="ml-2 text-gray-700 mon-400-12 whitespace-nowrap">
                          Upload From your computer
                        </span>
                      </div>
                    </Menu.Item>
                  </Menu>
                }
                trigger={["click"]}
              >
                <Tooltip title="Attachment">
                  <AttachmentIcon className="text-gray-700" />
                </Tooltip>
              </Dropdown>
            )}
          </div>
          {edit ? (
            <div className="flex justify-end gap-x-3">
              <CustomButton
                text="Cancel"
                className="text-gray-700 bg-white border border-gray-200 mon-500-14"
                height="36px"
                width="81px"
                onClick={() => dispatch(resetConversations(["editMessage"]))}
              />
              <CustomButton
                text="Save"
                className="text-white mon-500-14 bg-primary-600 "
                height="36px"
                width="62px"
                onClick={() => {
                  editMessage();
                  dispatch(resetConversations(["editMessage"]));
                }}
              />
            </div>
          ) : (
            <div className="flex items-center">
              {requestOption && (
                <button
                  className="mr-2"
                  onClick={() => setRequestOption(false)}
                >
                  <CrossIcon className="text-gray-500" />
                </button>
              )}
              <button
                className={`min-w-[30px] min-h-[30px] flex items-center justify-center ${value !== "" ? "bg-primary-600 rounded-[4px]" : ""}`}
                onClick={() => {
                  // Previously: `comments && !isFileUploading ?
                  // sendCommentsMessage() : sendMessage()`. That reads as
                  // "if in comments mode and not uploading, send a
                  // comment — otherwise send a regular message", so
                  // clicking Send from *within comments mode* while a
                  // file was still uploading fell into the else branch
                  // and called sendMessage() — the regular-thread sender,
                  // wrong function entirely for this mode, reaching for
                  // currentThread/threadMembers that comments mode
                  // doesn't set up. The upload check and the mode check
                  // were never meant to be chained like that; they're
                  // separate concerns.
                  if (isFileUploading) return;
                  dispatch(resetConversations(["unreadCount"]));
                  comments ? sendCommentsMessage() : sendMessage();
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.3333 1.66797L9.16667 10.8346M18.3333 1.66797L12.5 18.3346L9.16667 10.8346M18.3333 1.66797L1.66667 7.5013L9.16667 10.8346"
                    stroke={value !== "" ? "#ffff" : "#667085"}
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationsInput;